import { ProductCategoryFilterType } from 'src/enums'
import { DrawingFilters } from 'src/generated-types/configure-positions'
import { IPositionDrawing } from 'src/generated-types/position-drawing'
import { compareDrawingNumbers } from './configurePositions.action'

// Drawings saved without an explicit leg count are rendered with three legs (see MapSvgGenerator).
export const DEFAULT_BRIDLE_LEG_COUNT = 3
export const BRIDLE_LEG_OPTIONS = [1, 2, 3]

export const EMPTY_DRAWING_FILTERS: DrawingFilters = { bridleLegCount: null, componentCounts: {}, availability: {} }

interface FilterableCategory {
  id: EntityId
  name: string
  filterType?: number
}

export const splitFilterCategories = <T extends FilterableCategory>(categories: T[]) => {
  const existCategories = categories.filter(category => category.filterType === ProductCategoryFilterType.IsCheckExist)
  const typedCountCategories = categories.filter(
    category => category.filterType === ProductCategoryFilterType.IsFilterByCount,
  )
  // Categories that were never given a filter type fall back to a count filter, as in the drawing browser.
  const countCategories = typedCountCategories.length || existCategories.length ? typedCountCategories : categories
  return { countCategories, existCategories }
}

export const countComponentsByCategory = (drawing: IPositionDrawing): Record<string, number> => {
  if (drawing.productCategoryCounts) return drawing.productCategoryCounts
  const totals: Record<string, number> = {}
  drawing.positionDrawingProductNumbers?.forEach(item => {
    const categoryKey = String(item.productCategoryId)
    totals[categoryKey] = (totals[categoryKey] ?? 0) + item.count
  })
  return totals
}

const parseCountFilter = (value: string) => {
  const parsedCount = Number(value)
  return Number.isInteger(parsedCount) && parsedCount > 0 ? parsedCount : null
}

export const setBridleLegCount = (filters: DrawingFilters, legCount: number): DrawingFilters => {
  return { ...filters, bridleLegCount: filters.bridleLegCount === legCount ? null : legCount }
}

const withoutKey = <T>(record: Record<string, T>, key: string) => {
  return Object.fromEntries(Object.entries(record).filter(([existingKey]) => existingKey !== key))
}

export const setComponentCount = (filters: DrawingFilters, categoryId: string, value: string): DrawingFilters => {
  const remainingCounts = withoutKey(filters.componentCounts, categoryId)
  const componentCounts =
    parseCountFilter(value) === null ? remainingCounts : { ...remainingCounts, [categoryId]: value }
  return { ...filters, componentCounts }
}

export const setAvailability = (filters: DrawingFilters, categoryId: string, required: boolean): DrawingFilters => {
  const remainingAvailability = withoutKey(filters.availability, categoryId)
  return {
    ...filters,
    availability: required ? { ...remainingAvailability, [categoryId]: true } : remainingAvailability,
  }
}

export const countActiveFilters = (filters: DrawingFilters) => {
  return (
    (filters.bridleLegCount === null ? 0 : 1) +
    Object.keys(filters.componentCounts).length +
    Object.values(filters.availability).filter(Boolean).length
  )
}

export const filterDrawings = (
  drawings: IPositionDrawing[],
  { search, filters }: { search: string; filters: DrawingFilters },
) => {
  const normalizedSearch = search.trim().toLowerCase()
  return drawings
    .filter(drawing => {
      if (normalizedSearch && !drawing.drawingNumber.toLowerCase().includes(normalizedSearch)) return false
      if (
        filters.bridleLegCount !== null &&
        (drawing.bridleLegCount ?? DEFAULT_BRIDLE_LEG_COUNT) !== filters.bridleLegCount
      ) {
        return false
      }
      const componentTotals = countComponentsByCategory(drawing)
      const countsMatch = Object.entries(filters.componentCounts).every(
        ([categoryId, expectedCount]) => (componentTotals[categoryId] ?? 0) === Number(expectedCount),
      )
      const availabilityMatch = Object.entries(filters.availability).every(
        ([categoryId, required]) => !required || (componentTotals[categoryId] ?? 0) > 0,
      )
      return countsMatch && availabilityMatch
    })
    .sort(compareDrawingNumbers)
}
